/**
 * A barra da aba Exame (E205.F2.T03, ADR-0147 §2–§3), numa linha: à esquerda a troca de saída (exame
 * ou relatório de risco, em PARTE com risco) e o selo do texto; à direita "Documentos" (o original ao
 * lado) e o botão "Exame" com as três saídas (E207.F5.T03: a linha do pacote saiu). Abaixo, só o que pede
 * atenção: os avisos da emissão e a falha do ZIP. E205.F3: a leitura proposta sem conferência foi
 * para a aba Pendências (do servidor), e o aviso dela saiu daqui.
 *
 * Toma o lugar do `EstadoDoExame` da E91.F2 (o estado do caso segue em `data-estado`).
 */
import type { AxiosInstance } from 'axios';
import type { ReactNode } from 'react';

import { AvisosDaEmissao, useBaixarExame } from '@/components/ExameEletronico/BaixarExame';
import { estadoDoCaso } from '@/components/ExameEletronico/EstadoDoExame';
import {
  FalhaDoZipDito,
  useBaixarExameEArquivos,
} from '@/components/PreparacaoDoPacote/BaixarExameEArquivos';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico';
import { disponibilidadeDoExame } from '@/lib/exame_eletronico_saida';

import { MenuDoExame } from './MenuDoExame';
import type { AbaDoPainel } from './usePainelDaMesa';

const t = ptBR.mesaUnica;

export interface BarraDoExameProps {
  client: AxiosInstance;
  caseId: string;
  pecas: readonly ExameEletronicoCcbData[];
  temProtocolo: boolean;
  emProcessamento: boolean;
  bancoDoCaso: string | null | undefined;
  analiseFotograficaEmAndamento: boolean;
  /** A troca de saída e o selo do texto. */
  esquerda: ReactNode;
  painel: AbaDoPainel | null;
  onPainel: (aba: AbaDoPainel) => void;
  onPrevia: () => void;
  /** O ZIP achou a prévia sendo refeita: quem mostra o card relê. */
  onAtualizarPacote: () => void;
}

export function BarraDoExame(props: BarraDoExameProps): JSX.Element {
  const { client, caseId, pecas, emProcessamento } = props;
  const exame = useBaixarExame(caseId);
  const zip = useBaixarExameEArquivos(client, caseId, undefined, props.onAtualizarPacote);
  const documentosAbertos = props.painel === 'documentos';
  return (
    <div
      className="space-y-sc-2"
      data-testid="estado-do-exame"
      data-estado={estadoDoCaso(pecas, props.temProtocolo, emProcessamento)}
    >
      <div
        className="flex flex-wrap items-center justify-between gap-sc-3 border-b border-border pb-sc-3"
        data-testid="barra-do-exame"
      >
        <div className="flex flex-wrap items-center gap-sc-3">{props.esquerda}</div>
        <div className="flex flex-wrap items-center gap-sc-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-pressed={documentosAbertos}
            onClick={() => props.onPainel('documentos')}
            className={documentosAbertos ? 'bg-fg/[0.07]' : undefined}
            data-testid="mesa-acao-documentos"
          >
            {t.documento.abrir}
          </Button>
          <MenuDoExame
            onPrevia={props.onPrevia}
            exame={exame}
            disponibilidade={disponibilidadeDoExame(pecas, emProcessamento)}
            zip={zip}
          />
        </div>
      </div>
      <AvisosDaEmissao
        bancoDoCaso={props.bancoDoCaso}
        analiseFotograficaEmAndamento={props.analiseFotograficaEmAndamento}
        download={exame}
      />
      <FalhaDoZipDito falha={zip.falha} />
    </div>
  );
}
